import type { OrderMarginRow } from "../economicSummaryApi";

/** Riga "margine netto" nel blocco prezzo della card ordine (Davide
 *  2026-07-03): margine € e % calcolati dal motore economics coi parametri
 *  di Analisi Marketplace. Il breakdown costi completo sta nel `title`
 *  (tooltip nativo, zero stato): per l'analisi tabellare della selezione
 *  c'e' il dialog «Conteggio». Non renderizza nulla quando il margine non
 *  e' calcolabile (marketplace fuori AM) o non ancora caricato: meglio
 *  nessun numero che un numero falso. */

function euro(value: string | null): string {
  const parsed = Number.parseFloat(value ?? "");
  if (!Number.isFinite(parsed)) return "n.d.";
  return parsed.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function buildTitle(row: OrderMarginRow): string {
  const lines = [
    "Analisi costi (parametri Analisi Marketplace):",
    `Ricavo lordo: € ${euro(row.gross_revenue)}`,
    `Ricavo netto IVA: € ${euro(row.net_revenue)}`,
    `Commissione marketplace: −€ ${euro(row.commission)}`,
    `Spedizione: −€ ${euro(row.shipping)}`,
    row.packaging_in_supplier_cost
      ? `Imballo: € ${euro(row.packaging)} (già nel costo fornitore)`
      : `Imballo: −€ ${euro(row.packaging)}`,
    `Costo fornitore: −€ ${euro(row.supplier_cost ?? row.product_cost)}`,
    `= Margine netto: € ${euro(row.margin)} (${percent(row.margin_percent)})`,
  ];
  if (row.is_estimate && row.estimate_reasons.length > 0) {
    lines.push(`Stima: ${row.estimate_reasons.join(", ")}`);
  }
  return lines.join("\n");
}

function percent(value: string | null): string {
  const parsed = Number.parseFloat(value ?? "");
  if (!Number.isFinite(parsed)) return "n.d.";
  return `${parsed.toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

export function MarginNetLine({ row }: { row: OrderMarginRow | null | undefined }) {
  // Margine non calcolabile: prima qui si tornava `null` e la riga spariva. Ma
  // "nessun numero" e "numero zero" si vedono uguali — cioe' non si vedono — e
  // l'ordine sembrava semplicemente non conteggiato (Davide 2026-08-14,
  // O26-866934461898). Sono 1076 ordini su 4695, il 23%: un buco di quella
  // taglia deve dire di esserci, e soprattutto PERCHE', perche' il motivo piu'
  // frequente — costo mancante in Analisi Marketplace — lo puo' chiudere solo
  // Davide, e solo se sa quale prodotto guardare.
  // Resta invariata la regola di fondo: nessun numero inventato.
  if (row && !row.computable) {
    const motivi =
      row.estimate_reasons.length > 0 ? row.estimate_reasons.join(", ") : "dati incompleti";
    return (
      <div
        data-testid="margin-not-computable"
        title={`Margine non calcolabile: ${motivi}.\nI parametri arrivano da Analisi Marketplace: se manca il costo del prodotto, va compilato li'.`}
        className="text-[11px] whitespace-nowrap cursor-help text-ink"
      >
        netto n.d.
      </div>
    );
  }
  if (!row || row.margin === null) return null;
  const marginValue = Number.parseFloat(row.margin);
  const negative = Number.isFinite(marginValue) && marginValue < 0;
  return (
    <div
      data-testid="margin-net-line"
      title={buildTitle(row)}
      className={[
        // Semibold: il guadagno e' l'informazione decisionale primaria sotto
        // il prezzo (Davide 2026-07-03 punto 1).
        //
        // La riga USCIVA dalla colonna (Davide 2026-09-01, con la foto):
        // "netto ≈ € 92,19 · 17,6%" misurava 158px in una colonna da 132. Il
        // `truncate` messo allora come rete tagliava pero' il numero a meta'
        // (Davide 2026-09-23: «vengono tagliati i numeri»): ora importo e
        // percentuale sono due blocchi che non si spezzano, e quando non c'e'
        // posto la percentuale va a capo sotto, intera.
        "flex flex-wrap justify-end gap-x-1 text-[11px] font-semibold tabular-nums cursor-help",
        negative ? "text-red" : "text-green",
      ].join(" ")}
    >
      <span className="whitespace-nowrap">
        netto {row.is_estimate ? "≈ " : ""}€{euro(row.margin)}
      </span>
      <span className="whitespace-nowrap before:mr-1 before:content-['·']">
        {percent(row.margin_percent)}
      </span>
    </div>
  );
}
